import { firebaseConfig, APP_ID } from './firebase';

export type LeadInput = {
  name: string;
  phone: string;
  instagram?: string;
  gender?: 'male' | 'female';
  answers?: Record<string, string>;
};

type PendingLead = LeadInput & { createdAt: string };

const TIMEOUT_MS = 15000;
const PENDING_KEY = 'za_pending_leads';
const LEADS_URL = `https://firestore.googleapis.com/v1/projects/${firebaseConfig.projectId}/databases/(default)/documents/artifacts/${APP_ID}/public/data/leads?key=${firebaseConfig.apiKey}`;

const withTimeout = <T>(promise: Promise<T>, label: string): Promise<T> =>
  Promise.race([
    promise,
    new Promise<T>((_, reject) => setTimeout(() => reject(new Error(`${label} timed out`)), TIMEOUT_MS)),
  ]);

const str = (value: string) => ({ stringValue: value });

// The Firestore web SDK often never sends or never confirms a write from a
// freshly opened page (measured 3 of 5 lost); one REST request reports reliably.
function saveToDashboard(lead: PendingLead): Promise<void> {
  const answers = lead.answers || {};
  const answerFields = Object.keys(answers).reduce<Record<string, { stringValue: string }>>((acc, key) => {
    acc[key] = str(String(answers[key]));
    return acc;
  }, {});
  const fields: Record<string, unknown> = {
    name: str(lead.name),
    email: str(''),
    phone: str(lead.phone),
    instagram: str(lead.instagram || ''),
    quizAnswers: { mapValue: { fields: answerFields } },
    createdAt: str(lead.createdAt),
    status: str('new'),
  };
  if (lead.gender) fields.gender = str(lead.gender);

  return fetch(LEADS_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fields }),
  }).then((res) => {
    if (!res.ok) throw new Error(`Saving lead failed with status ${res.status}`);
  });
}

function sendEmail(lead: PendingLead): Promise<void> {
  return fetch('/api/notify', {
    method: 'POST',
    keepalive: true,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: lead.name,
      phone: lead.phone,
      instagram: lead.instagram || '',
      gender: lead.gender,
      answers: lead.answers || {},
    }),
  }).then((res) => {
    if (!res.ok) throw new Error(`Lead email failed with status ${res.status}`);
  });
}

async function sendLead(lead: PendingLead): Promise<boolean> {
  const [saved, emailed] = await Promise.allSettled([
    withTimeout(saveToDashboard(lead), 'Saving lead'),
    withTimeout(sendEmail(lead), 'Lead email'),
  ]);
  if (saved.status === 'rejected') console.error('Error saving lead:', saved.reason);
  if (emailed.status === 'rejected') console.error('Error sending lead email:', emailed.reason);
  return saved.status === 'fulfilled' || emailed.status === 'fulfilled';
}

const readPending = (): PendingLead[] => {
  try {
    return JSON.parse(localStorage.getItem(PENDING_KEY) || '[]');
  } catch {
    return [];
  }
};

const writePending = (leads: PendingLead[]) => {
  try {
    if (leads.length) localStorage.setItem(PENDING_KEY, JSON.stringify(leads));
    else localStorage.removeItem(PENDING_KEY);
  } catch {}
};

// Returns true if the lead reached Zayb by dashboard or email. If neither worked,
// the lead is kept in the visitor's browser and re-sent on their next visit.
export async function submitLead(input: LeadInput): Promise<boolean> {
  const lead: PendingLead = { ...input, createdAt: new Date().toISOString() };
  const ok = await sendLead(lead);
  if (!ok) writePending([...readPending(), lead]);
  return ok;
}

export async function retryPendingLeads(): Promise<void> {
  const pending = readPending();
  if (!pending.length) return;
  writePending([]);
  const stillFailing: PendingLead[] = [];
  for (const lead of pending) {
    if (!(await sendLead(lead))) stillFailing.push(lead);
  }
  writePending([...readPending(), ...stillFailing]);
}
